import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  VideoCameraIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  ArrowPathIcon,
  PlayIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { 
  getClassSettings, 
  updateInstructorStream, 
  setLiveBroadcastState, 
  getInstructors,
  subscribeToAttendanceRecords,
  getLocalDateString,
  subscribeToWeeklyQueries,
} from '../services/firebaseService';
import { ClassSettings, ClassScheduleEntry, InstructorStream, User, ClassAttendanceRecord } from '../types';
import ClassUpcomingNotificationBanner from '../components/common/ClassUpcomingNotificationBanner';
import { NotificationPermissionBanner, HeaderNotificationBell } from '../components/common/NotificationPermissionBanner';
import ClassChatBox from '../components/common/ClassChatBox';
import WatchingStatsPanel from '../components/common/WatchingStatsPanel';
import WeeklyQueriesInstructorView from '../components/common/WeeklyQueriesInstructorView';
import ExamsScheduleTab from '../components/common/ExamsScheduleTab';

const getYouTubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  const regExp = /(?:youtube\.com\/(?:watch\?v=|live\/|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regExp);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return match && match[1]
    ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&enablejsapi=1&origin=${encodeURIComponent(origin)}&rel=0&playsinline=1&modestbranding=1`
    : null;
};

const InstructorDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [instructor, setInstructor] = useState<any>(null);
  const [classSettings, setClassSettings] = useState<ClassSettings | null>(null);
  const [allInstructors, setAllInstructors] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'stream' | 'schedule' | 'directory' | 'chat' | 'viewers' | 'queries' | 'exams'>('stream');
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  const [weeklyQueriesCount, setWeeklyQueriesCount] = useState(0);

  // Stream Form State
  const [streamUrl, setStreamUrl] = useState('');
  const [streamTitle, setStreamTitle] = useState('');
  const [savingStream, setSavingStream] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  // Time & Live Broadcast state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [actionLoading, setActionLoading] = useState(false);

  // Auth verification
  useEffect(() => {
    const stored = localStorage.getItem('instructorUser');
    if (!stored) {
      // Check adminUser as fallback
      const adminStored = localStorage.getItem('adminUser');
      if (adminStored) {
        const parsedAdmin = JSON.parse(adminStored);
        if (['admin', 'super_admin'].includes(parsedAdmin.role)) {
          setInstructor(parsedAdmin);
          return;
        }
      }
      navigate('/instructor/login', { replace: true });
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      setInstructor(parsed);
    } catch {
      localStorage.removeItem('instructorUser');
      navigate('/instructor/login', { replace: true });
    }
  }, [navigate]);

  // Keep current time updated every second for live countdown
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch Class Settings and Instructors
  const fetchData = async () => {
    try {
      const [settings, instructors] = await Promise.all([
        getClassSettings(),
        getInstructors()
      ]);
      setClassSettings(settings);
      setAllInstructors(instructors);

      // Populate local stream state from settings if available
      if (instructor && settings?.instructors?.[instructor.uid]) {
        const myStream = settings.instructors[instructor.uid];
        setStreamUrl(myStream.streamUrl || '');
        setStreamTitle(myStream.streamTitle || '');
      }
    } catch (err) {
      console.error('Error loading instructor data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (instructor) {
      fetchData();
      const interval = setInterval(fetchData, 20000); // Poll every 20s
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructor]);

  // Real-time weekly queries count for instructor badge
  useEffect(() => {
    const unsub = subscribeToWeeklyQueries((list) => {
      setWeeklyQueriesCount(list.length);
    });
    return () => unsub();
  }, []);

  // Real-time attendance subscription for viewer count
  useEffect(() => {
    const todayStr = getLocalDateString();
    let cachedRecords: ClassAttendanceRecord[] = [];

    const recount = (records: ClassAttendanceRecord[]) => {
      const nowSec = Math.floor(Date.now() / 1000);
      const live = records.filter(
        (r) => r.sessionDate === todayStr && (nowSec - (r.lastSeenAt?.seconds || 0)) <= 90
      );
      setLiveViewerCount(live.length);
    };

    const unsub = subscribeToAttendanceRecords((records) => {
      cachedRecords = records;
      recount(records);
    }, todayStr);

    const timer = setInterval(() => {
      recount(cachedRecords);
    }, 15000);

    return () => {
      unsub();
      clearInterval(timer);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('instructorUser');
    navigate('/instructor/login', { replace: true });
  };

  const handleSaveStream = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!instructor) return;

    setSavingStream(true);
    setSaveSuccess(false);

    try {
      await updateInstructorStream(instructor.uid, {
        instructorName: instructor.displayName || instructor.email,
        instructorEmail: instructor.email,
        streamUrl: streamUrl.trim(),
        streamTitle: streamTitle.trim() || `Lecture Session by ${instructor.displayName || instructor.email}`
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      await fetchData();
    } catch (err: any) {
      console.error('Failed to save stream:', err);
      alert('Error saving stream settings: ' + err.message);
    } finally {
      setSavingStream(false);
    }
  };

  // Find assigned schedules for this instructor
  const myAssignedSchedules = (classSettings?.scheduleEntries || []).filter(
    (entry: ClassScheduleEntry) => 
      entry.instructorId === instructor?.uid || 
      (!entry.instructorId && ['admin', 'super_admin'].includes(instructor?.role))
  );

  // Check 30-minute schedule rule
  const isAdmin = ['admin', 'super_admin'].includes(instructor?.role);

  const getUpcomingScheduleStatus = () => {
    const now = currentTime.getTime();

    // Check all assigned schedules (or all schedules for admin)
    const schedulesToCheck = isAdmin 
      ? (classSettings?.scheduleEntries || [])
      : myAssignedSchedules;

    if (schedulesToCheck.length === 0) {
      return {
        canGoLive: isAdmin, // Admin can always go live
        reason: isAdmin ? 'Admin Override: Full broadcast control' : 'No upcoming classes assigned to your account.',
        nextSchedule: null,
        minutesUntil: null,
        unlockTime: null
      };
    }

    // Sort by scheduledAt ascending
    const sorted = [...schedulesToCheck].map(s => {
      const date = s.scheduledAt?.toDate ? s.scheduledAt.toDate() : new Date(s.scheduledAt);
      return { ...s, parsedDate: date };
    }).sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

    // Find the current or next upcoming schedule
    // A schedule is active if now is between [scheduledAt - 30 mins] and [scheduledAt + duration + 60 mins]
    for (const sched of sorted) {
      const schedTime = sched.parsedDate.getTime();
      const durationMs = (sched.durationMinutes || 60) * 60 * 1000;
      const unlockTime = new Date(schedTime - 30 * 60 * 1000);
      const endTime = new Date(schedTime + durationMs + 60 * 60 * 1000);

      // Are we currently within the 30-minute pre-class or in-class window?
      if (now >= unlockTime.getTime() && now <= endTime.getTime()) {
        return {
          canGoLive: true,
          reason: `Schedule active: ${sched.title}`,
          nextSchedule: sched,
          minutesUntil: 0,
          unlockTime
        };
      }

      // If scheduled in the future (more than 30 mins away)
      if (now < unlockTime.getTime()) {
        const diffMinutes = Math.round((schedTime - now) / 60000);
        return {
          canGoLive: isAdmin,
          reason: isAdmin 
            ? `Admin Override (Next class in ${diffMinutes}m)` 
            : `Scheduled for ${sched.parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Go Live unlocks 30 mins prior at ${unlockTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`,
          nextSchedule: sched,
          minutesUntil: diffMinutes,
          unlockTime
        };
      }
    }

    return {
      canGoLive: isAdmin,
      reason: isAdmin ? 'Admin Override: Full broadcast control' : 'All scheduled classes for today have concluded.',
      nextSchedule: sorted[sorted.length - 1] || null,
      minutesUntil: null,
      unlockTime: null
    };
  };

  const scheduleRule = getUpcomingScheduleStatus();
  const isCurrentlyLive = Boolean(classSettings?.isLiveNow);
  const isMyStreamLive = Boolean(isCurrentlyLive && classSettings?.activeInstructorId === instructor?.uid);

  // Toggle Live Handler
  const handleToggleLive = async (forceStart?: boolean | React.MouseEvent) => {
    if (!classSettings) return;
    const isForce = typeof forceStart === 'boolean' ? forceStart : false;
    const nextLive = isForce ? true : !isCurrentlyLive;

    if (nextLive) {
      if (!streamUrl.trim()) {
        alert('Please enter and save your YouTube Live Stream URL before starting the broadcast.');
        return;
      }

      if (!scheduleRule.canGoLive && !isAdmin) {
        alert('You cannot go live at this time: ' + scheduleRule.reason);
        return;
      }

      if (isCurrentlyLive && !isMyStreamLive) {
        const confirmSwitch = window.confirm(
          `Another instructor (${classSettings?.activeInstructorName || 'Faculty'}) is currently broadcasting live. Do you want to switch the student broadcast feed to your stream?`
        );
        if (!confirmSwitch) return;
      }
    } else {
      if (isCurrentlyLive && !isMyStreamLive && !isAdmin) {
        alert(`You cannot end another instructor's broadcast (${classSettings?.activeInstructorName || 'Faculty'}). Only the active broadcaster or an administrator can end the session.`);
        return;
      }
    }

    setActionLoading(true);
    try {
      const activeTitle = streamTitle.trim() || 
        scheduleRule.nextSchedule?.title || 
        `Live Training Session by ${instructor?.displayName || instructor?.email}`;

      await setLiveBroadcastState({
        isLiveNow: nextLive,
        youtubeUrl: streamUrl.trim(),
        liveLabel: activeTitle,
        instructorId: instructor?.uid,
        instructorName: instructor?.displayName || instructor?.email
      });

      await fetchData();
    } catch (err: any) {
      console.error('Error toggling live broadcast:', err);
      alert('Failed to update live status: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const embedPreviewUrl = getYouTubeEmbedUrl(streamUrl);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300 font-medium">Loading Instructor Portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Top Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Portal Title */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/20">
                <VideoCameraIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="font-bold text-lg text-white tracking-tight">Kreed Odisha</h1>
                  <span className="bg-indigo-500/20 text-indigo-300 text-xs px-2.5 py-0.5 rounded-full font-semibold border border-indigo-500/30">
                    Instructor Portal
                  </span>
                </div>
                <p className="text-xs text-slate-400">Live Classroom Studio & Stream Management</p>
              </div>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <div className="text-sm font-semibold text-white flex items-center space-x-1.5">
                  <span>{instructor?.displayName || instructor?.email}</span>
                  {isAdmin && (
                    <span className="bg-purple-500/20 text-purple-300 text-[10px] px-1.5 py-0.2 rounded font-mono uppercase">
                      Admin
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400">{instructor?.email}</div>
              </div>

              <HeaderNotificationBell />

              <button
                onClick={fetchData}
                disabled={refreshing}
                title="Refresh Status"
                className="p-2 text-slate-400 hover:text-white bg-slate-800/80 hover:bg-slate-800 rounded-lg border border-slate-700 transition-colors"
              >
                <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={handleLogout}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-medium border border-slate-700 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Browser Notification Permission Banner (Asks Chrome with explicit user gesture) */}
        <NotificationPermissionBanner role="instructor" />

        {/* 20-Minute Upcoming Class Alert for Instructors */}
        <ClassUpcomingNotificationBanner
          schedules={classSettings?.scheduleEntries || []}
          role="instructor"
          instructorId={instructor?.uid}
          onGoLiveClick={handleToggleLive}
          isCurrentlyLive={isCurrentlyLive}
        />

        {/* Live Broadcast Status Banner & Go Live Action Card */}
        <div className={`p-6 rounded-2xl border transition-all duration-300 ${
          isCurrentlyLive
            ? 'bg-gradient-to-r from-red-950/40 via-red-900/30 to-slate-900 border-red-500/40 shadow-xl shadow-red-950/30'
            : 'bg-slate-900/60 border-slate-800'
        }`}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <span className={`inline-block w-3.5 h-3.5 rounded-full ${
                  isCurrentlyLive ? 'bg-red-500 animate-ping' : 'bg-slate-500'
                }`} />
                <h2 className="text-xl font-bold tracking-tight text-white flex items-center space-x-2">
                  <span>Broadcast Status:</span>
                  <span className={isCurrentlyLive ? 'text-red-400' : 'text-slate-400'}>
                    {isCurrentlyLive ? '🔴 LIVE ON STUDENT PORTAL' : '⚪ OFFLINE'}
                  </span>
                </h2>
              </div>

              {/* Viewer count badge */}
              {isCurrentlyLive && liveViewerCount > 0 && (
                <div className="flex items-center space-x-2 text-sm">
                  <span className="flex items-center space-x-1.5 px-3 py-1 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 font-semibold">
                    <EyeIcon className="w-4 h-4" />
                    <span>{liveViewerCount} watching live</span>
                  </span>
                </div>
              )}

              {isCurrentlyLive ? (
                <div className="text-sm text-slate-300 space-y-1">
                  <p>
                    Session: <strong className="text-white">{classSettings?.liveLabel}</strong>
                  </p>
                  <p className="text-xs text-slate-400">
                    Active Broadcaster:{' '}
                    <span className="text-indigo-300 font-medium">
                      {classSettings?.activeInstructorName || 'Instructor'}
                    </span>{' '}
                    {isMyStreamLive && <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded text-[11px] font-semibold border border-emerald-500/30 ml-1">Your Stream</span>}
                  </p>
                </div>
              ) : (
                <div className="text-sm text-slate-300 space-y-1">
                  <p>
                    No stream is currently active. When you start the broadcast, registered students visiting <span className="font-mono text-indigo-300 font-semibold">/class/dashboard</span> will see your feed.
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-slate-400">
                    <ClockIcon className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <span>{scheduleRule.reason}</span>
                  </div>
                </div>
              )}
            </div>


            {/* Go Live / End Broadcast Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
              <Link
                to="/class/dashboard"
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl text-sm font-medium border border-slate-700 transition-colors text-center"
              >
                View Student Dashboard &rarr;
              </Link>

              {isCurrentlyLive ? (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {!isMyStreamLive && (
                    <button
                      type="button"
                      onClick={() => handleToggleLive(true)}
                      disabled={actionLoading || (!scheduleRule.canGoLive && !isAdmin)}
                      className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-emerald-600/25 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                      title="Switch student broadcast to your stream"
                    >
                      <PlayIcon className="w-4 h-4" />
                      <span>{actionLoading ? 'Switching...' : 'Switch to My Stream'}</span>
                    </button>
                  )}
                  {(isMyStreamLive || isAdmin) && (
                    <button
                      type="button"
                      onClick={() => handleToggleLive(false)}
                      disabled={actionLoading}
                      className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-red-600/30 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <span className="w-2.5 h-2.5 bg-white rounded-full animate-pulse" />
                      <span>{actionLoading ? 'Updating...' : 'End Live Broadcast'}</span>
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleToggleLive}
                  disabled={actionLoading || (!scheduleRule.canGoLive && !isAdmin)}
                  className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center space-x-2 shadow-lg ${
                    scheduleRule.canGoLive || isAdmin
                      ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white shadow-emerald-600/25 cursor-pointer'
                      : 'bg-slate-800 text-slate-400 border border-slate-700/50 cursor-not-allowed'
                  }`}
                  title={!scheduleRule.canGoLive && !isAdmin ? scheduleRule.reason : 'Start broadcasting to students'}
                >
                  {scheduleRule.canGoLive || isAdmin ? (
                    <>
                      <PlayIcon className="w-4 h-4" />
                      <span>{actionLoading ? 'Starting...' : 'Go Live (Start Broadcast)'}</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="w-4 h-4 text-slate-500" />
                      <span>Go Live (Locked - 30m Before Schedule)</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-800 space-x-1 sm:space-x-4">
          <button
            onClick={() => setActiveTab('stream')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'stream'
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <VideoCameraIcon className="w-4 h-4" />
            <span>My Stream & Live Preview</span>
          </button>

          <button
            onClick={() => setActiveTab('schedule')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'schedule'
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <CalendarDaysIcon className="w-4 h-4" />
            <span>Assigned Class Schedules</span>
            {myAssignedSchedules.length > 0 && (
              <span className="bg-indigo-500/20 text-indigo-300 text-xs px-1.5 py-0.5 rounded-full font-mono">
                {myAssignedSchedules.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('queries')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'queries'
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <QuestionMarkCircleIcon className="w-4 h-4 text-warning-400" />
            <span>Weekly Student Queries</span>
            {weeklyQueriesCount > 0 && (
              <span className="bg-warning-500/20 text-warning-300 border border-warning-500/30 text-xs px-2 py-0.5 rounded-full font-mono font-bold">
                {weeklyQueriesCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('directory')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'directory'
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <UserGroupIcon className="w-4 h-4" />
            <span>All Instructors & Streams</span>
            {allInstructors.length > 0 && (
              <span className="bg-slate-800 text-slate-400 text-xs px-1.5 py-0.5 rounded-full font-mono">
                {allInstructors.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('chat')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'chat'
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            <span>Live Class Chat</span>
          </button>

          <button
            onClick={() => setActiveTab('viewers')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'viewers'
                ? 'border-emerald-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <EyeIcon className="w-4 h-4" />
            <span>Viewers & Stats</span>
            {liveViewerCount > 0 && (
              <span className="bg-emerald-500/20 text-emerald-300 text-xs px-1.5 py-0.5 rounded-full font-mono border border-emerald-500/20">
                {liveViewerCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('exams')}
            className={`pb-3 px-3 text-sm font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'exams'
                ? 'border-amber-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <AcademicCapIcon className="w-4 h-4 text-amber-400" />
            <span>Exams Schedule</span>
            <span className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] px-1.5 py-0.5 rounded-full font-bold">
              Coming Soon
            </span>
          </button>
        </div>

        {/* TAB: Weekly Student Queries */}
        {activeTab === 'queries' && (
          <WeeklyQueriesInstructorView instructor={instructor} />
        )}

        {/* TAB: Exams Schedule & Benchmarks */}
        {activeTab === 'exams' && (
          <div className="space-y-6">
            <ExamsScheduleTab />
          </div>
        )}

        {/* TAB 5: Viewers & Stats */}
        {activeTab === 'viewers' && (
          <div className="space-y-4">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <div className="mb-6">
                <h3 className="text-base font-bold text-white tracking-tight flex items-center space-x-2">
                  <EyeIcon className="w-5 h-5 text-emerald-400" />
                  <span>Live Viewers & Attendance Statistics</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Real-time attendance tracking — updates live as students watch. Students see one unified player; this panel breaks down viewing by class session.
                </p>
              </div>
              <WatchingStatsPanel
                classSettings={classSettings}
                isAdmin={isAdmin}
              />
            </div>
          </div>
        )}

        {/* TAB 1: My Stream & Preview */}
        {activeTab === 'stream' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Stream URL Form */}
              <div className="lg:col-span-6 space-y-4">
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-white tracking-tight flex items-center space-x-2">
                      <VideoCameraIcon className="w-5 h-5 text-indigo-400" />
                      <span>YouTube Live Stream Settings</span>
                    </h3>
                    {isMyStreamLive && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 flex items-center space-x-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                        <span>LIVE NOW</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mb-6 leading-relaxed">
                    Paste your YouTube Live Broadcast URL or unlisted lecture video link. When students join your class, they will view this video.
                  </p>

                  {saveSuccess && (
                    <div className="mb-4 p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center space-x-2 animate-fade-in">
                      <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                      <span>Stream configuration saved successfully!</span>
                    </div>
                  )}

                  <form onSubmit={handleSaveStream} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        YouTube Live / Video URL *
                      </label>
                      <input
                        type="url"
                        required
                        value={streamUrl}
                        onChange={(e) => setStreamUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                        className="w-full px-3.5 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Supports full YouTube URLs (<span className="font-mono">youtube.com/watch?v=...</span>), live streams (<span className="font-mono">youtube.com/live/...</span>), or short links (<span className="font-mono">youtu.be/...</span>).
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        Broadcast Topic / Label (Optional)
                      </label>
                      <input
                        type="text"
                        value={streamTitle}
                        onChange={(e) => setStreamTitle(e.target.value)}
                        placeholder="e.g. Football Coaching — Tactical Systems & Formations"
                        className="w-full px-3.5 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Displayed on top of the student video player during the broadcast.
                      </p>
                    </div>

                    <div className="pt-2 flex justify-end">
                      <button
                        type="submit"
                        disabled={savingStream}
                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-600/20 transition-all disabled:opacity-50"
                      >
                        {savingStream ? 'Saving Stream...' : 'Save Stream Link'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Instructions Card */}
                <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 text-xs text-slate-400 space-y-2">
                  <div className="flex items-center space-x-2 text-indigo-300 font-semibold">
                    <InformationCircleIcon className="w-4 h-4" />
                    <span>How Streaming Works</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-slate-400 leading-relaxed">
                    <li>Start your stream in OBS Studio or YouTube Studio connected to YouTube.</li>
                    <li>Set the YouTube privacy to <strong>Unlisted</strong> or <strong>Public</strong>.</li>
                    <li>Copy the YouTube link and paste it into the field above, then click <strong>Save Stream Link</strong>.</li>
                    <li>Check the live preview player on the right to verify video playback.</li>
                    <li>When you are within <strong>30 minutes</strong> of your assigned schedule, click <strong>Go Live</strong> to broadcast to all students!</li>
                  </ul>
                </div>
              </div>

              {/* Stream Live Preview Player & Live Class Chat */}
              <div className="lg:col-span-6 space-y-4">
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-white tracking-tight flex items-center space-x-2">
                      <PlayIcon className="w-5 h-5 text-indigo-400" />
                      <span>Live Embed Preview</span>
                    </h3>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => setPreviewKey((k) => k + 1)}
                        className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-300 hover:text-white border border-slate-700 transition flex items-center space-x-1"
                        title="Reload stream preview"
                      >
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                        <span>Reload Preview</span>
                      </button>
                    </div>
                  </div>

                  <div className="aspect-video w-full bg-black rounded-xl overflow-hidden border border-slate-800 flex items-center justify-center relative shadow-inner">
                    {embedPreviewUrl ? (
                      <iframe
                        key={previewKey}
                        src={embedPreviewUrl}
                        title="Instructor Live Feed Preview"
                        className="w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    ) : (
                      <div className="text-center p-6 space-y-2">
                        <VideoCameraIcon className="w-12 h-12 text-slate-700 mx-auto" />
                        <p className="text-sm text-slate-400 font-medium">No Stream URL Configured</p>
                        <p className="text-xs text-slate-500 max-w-xs mx-auto">
                          Paste a valid YouTube link on the left to preview your live stream player.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                    <span>Student View Mode: <strong className="text-slate-200">16:9 HD Embed</strong></span>
                    {isMyStreamLive && (
                      <span className="text-red-400 font-semibold flex items-center space-x-1">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
                        <span>Your Feed is Broadcasted Live</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Real-Time Live Classroom Chat for Instructor */}
                <ClassChatBox
                  currentUser={{
                    name: instructor?.displayName || instructor?.email || 'Instructor',
                    id: instructor?.uid || 'instructor',
                    role: 'instructor',
                    badge: 'Faculty / Instructor',
                  }}
                  isInstructor={true}
                  theme="slate"
                  maxHeightClass="h-[460px] sm:h-[500px]"
                  viewerCount={liveViewerCount}
                  youtubeUrl={streamUrl || classSettings?.youtubeUrl}
                />
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: Assigned Class Schedules */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white tracking-tight">Your Assigned Schedules</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Classes assigned to you by administrators. Go Live unlocks 30 minutes before start time.
                  </p>
                </div>
                <div className="text-xs px-3 py-1.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-xl font-medium">
                  Current Time: {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
              </div>

              {myAssignedSchedules.length > 0 ? (
                <div className="space-y-3">
                  {myAssignedSchedules.map((entry: ClassScheduleEntry) => {
                    const schedDate = entry.scheduledAt?.toDate ? entry.scheduledAt.toDate() : new Date(entry.scheduledAt);
                    const now = currentTime.getTime();
                    const schedTime = schedDate.getTime();
                    const unlockTime = new Date(schedTime - 30 * 60 * 1000);
                    const durationMs = (entry.durationMinutes || 60) * 60 * 1000;
                    const endTime = new Date(schedTime + durationMs + 60 * 60 * 1000);

                    const isWindowActive = now >= unlockTime.getTime() && now <= endTime.getTime();
                    const isFuture = now < unlockTime.getTime();

                    return (
                      <div
                        key={entry.id}
                        className={`p-4 rounded-xl border transition-colors ${
                          isWindowActive
                            ? 'bg-emerald-950/20 border-emerald-500/30'
                            : 'bg-slate-900/40 border-slate-800'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-sm text-white">{entry.title}</h4>
                              {entry.batchLabel && (
                                <span className="bg-indigo-500/20 text-indigo-300 text-xs px-2 py-0.5 rounded font-medium border border-indigo-500/30">
                                  {entry.batchLabel}
                                </span>
                              )}
                              {isWindowActive ? (
                                <span className="bg-emerald-500/20 text-emerald-300 text-xs px-2 py-0.5 rounded-full font-semibold border border-emerald-500/30">
                                  🟢 Go Live Unlocked
                                </span>
                              ) : isFuture ? (
                                <span className="bg-slate-800 text-slate-400 text-xs px-2 py-0.5 rounded-full font-medium flex items-center space-x-1">
                                  <LockClosedIcon className="w-3 h-3" />
                                  <span>Unlocks at {unlockTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </span>
                              ) : (
                                <span className="bg-slate-800 text-slate-500 text-xs px-2 py-0.5 rounded-full">
                                  Concluded
                                </span>
                              )}
                            </div>

                            {entry.topic && (
                              <p className="text-xs text-slate-400">Topic: {entry.topic}</p>
                            )}

                            <div className="flex items-center space-x-3 text-xs text-slate-400 pt-1">
                              <span>📅 Scheduled: <strong>{schedDate.toLocaleString()}</strong></span>
                              <span>&bull;</span>
                              <span>⏱️ Duration: <strong>{entry.durationMinutes} mins</strong></span>
                            </div>
                          </div>

                          <div>
                            {isWindowActive && !isCurrentlyLive && (
                              <button
                                type="button"
                                onClick={handleToggleLive}
                                disabled={actionLoading}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold text-xs transition-colors shadow-md shadow-emerald-600/20"
                              >
                                Go Live for this Session
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-950/40 rounded-xl border border-slate-800 space-y-2">
                  <CalendarDaysIcon className="w-10 h-10 text-slate-700 mx-auto" />
                  <p className="text-sm text-slate-400 font-medium">No Class Sessions Assigned Yet</p>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Administrators assign instructors to upcoming class slots in the Admin Settings panel. You will see your classes listed here once scheduled.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: All Instructors & Streams Directory */}
        {activeTab === 'directory' && (
          <div className="space-y-6">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <div className="mb-6">
                <h3 className="text-base font-bold text-white tracking-tight">Organization Instructors & Stream Status</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Overview of all registered instructors and their configured live stream channels.
                </p>
              </div>

              {allInstructors.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allInstructors.map((inst) => {
                    const instStream: InstructorStream | undefined = classSettings?.instructors?.[inst.uid];
                    const hasStreamUrl = Boolean(instStream?.streamUrl);
                    const isBroadcastingNow = isCurrentlyLive && classSettings?.activeInstructorId === inst.uid;

                    return (
                      <div
                        key={inst.uid}
                        className={`p-5 rounded-xl border transition-all ${
                          isBroadcastingNow
                            ? 'bg-red-950/20 border-red-500/40'
                            : 'bg-slate-900/40 border-slate-800'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold text-sm text-white">
                                {inst.displayName || inst.email}
                              </h4>
                              {inst.uid === instructor?.uid && (
                                <span className="bg-indigo-500/20 text-indigo-300 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase">
                                  You
                                </span>
                              )}
                              {isBroadcastingNow ? (
                                <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full font-bold border border-red-500/30 animate-pulse">
                                  🔴 LIVE NOW
                                </span>
                              ) : (
                                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                                  hasStreamUrl ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-800 text-slate-500'
                                }`}>
                                  {hasStreamUrl ? 'Stream Ready' : 'No Stream Link'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400">{inst.email}</p>
                          </div>

                          <span className={`w-2.5 h-2.5 rounded-full mt-1 ${
                            isBroadcastingNow ? 'bg-red-500 animate-ping' : hasStreamUrl ? 'bg-emerald-400' : 'bg-slate-600'
                          }`} />
                        </div>

                        {hasStreamUrl && (
                          <div className="mt-3 pt-3 border-t border-slate-800/80 space-y-1 text-xs">
                            {instStream?.streamTitle && (
                              <p className="text-slate-300">
                                <span className="text-slate-500">Title:</span> {instStream.streamTitle}
                              </p>
                            )}
                            <div className="flex items-center justify-between text-slate-500">
                              <span className="truncate max-w-xs font-mono text-[11px] opacity-70">{instStream?.streamUrl}</span>
                              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-semibold border border-emerald-500/30">
                                Protected Stream
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-950/40 rounded-xl border border-slate-800">
                  <UserGroupIcon className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No other instructor accounts found.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: Live Classroom Chat (Dedicated Moderation View) */}
        {activeTab === 'chat' && (
          <div className="space-y-4">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white tracking-tight flex items-center space-x-2">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-indigo-400" />
                    <span>Live Classroom Interaction & Moderation</span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Real-time chat with students participating in your live lecture without YouTube. Send announcements, answer queries, or moderate messages.
                  </p>
                </div>
              </div>

              <ClassChatBox
                currentUser={{
                  name: instructor?.displayName || instructor?.email || 'Instructor',
                  id: instructor?.uid || 'instructor',
                  role: 'instructor',
                  badge: 'Faculty / Instructor',
                }}
                isInstructor={true}
                theme="slate"
                maxHeightClass="h-[600px]"
                viewerCount={liveViewerCount}
                youtubeUrl={streamUrl || classSettings?.youtubeUrl}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InstructorDashboardPage;
